import { Button, InfoSheetPanel, Paragraph, Row, Separator } from '@fs/zion-ui'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ControlHighlight } from '@fs/zion-icon'
import { useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import PanelHeader from '../shared/PanelHeader'
import HighlightsTipsOld from './HighlightsTipsOld'
import { getFieldValue } from './utils/highlightUtils'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'

const HighlightPanelOld = ({ splitScreenImageArk, onSave: onSaveProp, onCancel: onCancelProp }) => {
  const [t] = useTranslation()
  const { highlightsPanelOldOverlay, closeOverlay, topMostPanel } = useOverlayContext()
  const { currentImageArk, types } = useSlsStateContext()
  const {
    setCreatingRects,
    setEditMode,
    setDisplayHighlights,
    stageEdits,
    setActiveFieldInfo,
    activeFieldInfo,
    onSave,
    clearDisplay,
    getHighlights,
    clearEdits,
  } = useSlsHighlightsContext()

  const isEditing = useMemo(
    () => getHighlights().mainEditHighlights.find((h) => h.fieldId === activeFieldInfo.id),
    [getHighlights, activeFieldInfo.id]
  )

  const resetSlsHighlightState = useCallback(
    (resetActiveFieldInfo = true) => {
      setCreatingRects(false)
      setEditMode(false)
      if (resetActiveFieldInfo) setActiveFieldInfo({ id: null, value: null })
    },
    [setCreatingRects, setEditMode, setActiveFieldInfo]
  )

  useEffect(() => {
    if (highlightsPanelOldOverlay.isOpen && topMostPanel() === 'highlightsPanelOld') {
      setEditMode(true)

      setDisplayHighlights({
        mainPartitionKey: currentImageArk,
        mainZoomFilter: (highlight) => highlight.attached,
        attachFilter: (highlight) => highlight.fieldId === activeFieldInfo.id,
      })

      if (splitScreenImageArk) {
        setDisplayHighlights({
          splitScreenPartitionKey: splitScreenImageArk,
          splitScreenZoomFilter: (highlight) => highlight.attached,
          attachFilter: (highlight) => highlight.fieldId === activeFieldInfo.id,
        })
      }
    }
  }, [
    highlightsPanelOldOverlay.isOpen,
    currentImageArk,
    activeFieldInfo?.id,
    topMostPanel,
    splitScreenImageArk,
    setDisplayHighlights,
    setEditMode,
  ])

  const title = isEditing
    ? t('editHighlight.panelHeader.panelHeadingEdit', 'Edit Highlight')
    : t('editHighlight.panelHeader.panelHeadingAdd', 'Add Highlight')
  const handleCancel = useCallback(() => {
    clearEdits()
    clearDisplay({ all: true })
    resetSlsHighlightState()
    closeOverlay('highlightsPanelOld')
    onCancelProp?.()
  }, [resetSlsHighlightState, closeOverlay, clearDisplay, onCancelProp, clearEdits])

  const handleSave = useCallback(() => {
    // Helper function to filter attached highlights
    const getAttachedHighlights = (highlights) => highlights?.filter((h) => h?.attached === true)

    const { mainEditHighlights, splitScreenEditHighlights } = stageEdits()
    const mainSelectedHighlights = getAttachedHighlights(mainEditHighlights)
    const splitScreenSelectedHighlights = getAttachedHighlights(splitScreenEditHighlights)
    const fieldType = types?.fields.find((type) => type.fieldType === activeFieldInfo?.fieldType)

    if (onSave) {
      // Running the onSave function from the hook so it can handle things like checking for shared highlights
      onSave({
        mainEditHighlights,
        splitScreenEditHighlights,
        mainSelectedHighlights,
        splitScreenSelectedHighlights,
        fieldType,
        activeFieldInfo,
      })
      resetSlsHighlightState(false)
      onSaveProp?.() // Notify parent component if needed
    } else {
      resetSlsHighlightState()
      closeOverlay('highlightsPanelOld')
      onSaveProp?.() // Notify parent component if needed
    }
  }, [stageEdits, types, onSave, closeOverlay, activeFieldInfo, resetSlsHighlightState, onSaveProp])

  const instructions = t('editHighlight.instructions', 'Select the {fieldType} "{fieldValue}".', {
    fieldType: activeFieldInfo?.label || activeFieldInfo?.displayLabel,
    fieldValue: activeFieldInfo?.fieldType === 'EVENT_TYPE' ? '' : getFieldValue({ activeFieldInfo, t }),
  })

  return (
    <InfoSheetPanel
      headingColor="yellow"
      title={title}
      {...highlightsPanelOldOverlay}
      close={handleCancel}
      footer={
        <Row alignX="end">
          <Button onClick={handleCancel} linkName="Edit Highlights Cancel" data-testid="highlights-panel-cancel-button">
            {t('edit-index.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            onClick={handleSave}
            linkName="Edit Highlights Save"
            data-testid="highlights-panel-save-button"
          >
            {t('edit-index.update.button', 'Update')}
          </Button>
        </Row>
      }
    >
      <PanelHeader
        heading={title}
        caption={t('editHighlight.panelHeader.panelCaption', 'Select the blue highlights or create your own')}
      />
      <HighlightsTipsOld />
      <Separator size="xs" />
      {/* Remove empty string from displaying */}
      <Paragraph size="sm">{(instructions || '').replace(' "".', '.')}</Paragraph>
      <Separator size="xs" />
      <Button
        Icon={ControlHighlight}
        onClick={() => setCreatingRects(true)}
        data-testid="highlights-panel-new-highlight"
        linkName="Edit Highlights New Highlight"
      >
        {t('editHighlight.highlightTips.newHighlight', 'New Highlight')}
      </Button>
      <Separator size="xs" />
    </InfoSheetPanel>
  )
}

export default HighlightPanelOld
